import { useState, useEffect, useCallback } from 'react';
import { getAvailabilityEvents, getAvailabilityStats } from '../api/client';

interface AvailabilityEvent {
  id: string;
  pinSku: string;
  pinName?: string;
  storeId: string;
  storeName?: string;
  siteName?: string;
  eventType: string;
  scanMethod?: string;
  quantity?: number;
  notes?: string;
  createdAt: string;
}

interface AvailabilityStats {
  eventsToday: number;
  eventsThisWeek: number;
  totalEvents: number;
  eventsByType: Record<string, number>;
}

export default function Availability() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<AvailabilityStats>({
    eventsToday: 0,
    eventsThisWeek: 0,
    totalEvents: 0,
    eventsByType: {},
  });
  const [events, setEvents] = useState<AvailabilityEvent[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statsResult, eventsResult] = await Promise.all([
        getAvailabilityStats().catch(() => ({ data: { eventsToday: 0, eventsThisWeek: 0, totalEvents: 0, eventsByType: {} } })),
        getAvailabilityEvents(page, 20, eventTypeFilter || undefined),
      ]);

      setStats({
        eventsToday: statsResult.data?.eventsToday || 0,
        eventsThisWeek: statsResult.data?.eventsThisWeek || 0,
        totalEvents: statsResult.data?.totalEvents || 0,
        eventsByType: statsResult.data?.eventsByType || {},
      });

      setEvents(eventsResult.data?.events || []);
      const total = eventsResult.data?.pagination?.total || 0;
      setTotalPages(Math.ceil(total / 20) || 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [page, eventTypeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case 'scan': return '&#x1F4F7;';
      case 'confirmed_available': return '&#x2705;';
      case 'confirmed_unavailable': return '&#x274C;';
      case 'purchase': return '&#x1F6D2;';
      default: return '&#x1F4CA;';
    }
  };

  const getEventTypeBadgeClasses = (type: string) => {
    switch (type) {
      case 'scan': return 'bg-orange-100 text-orange-700';
      case 'confirmed_available': return 'bg-green-100 text-green-700';
      case 'confirmed_unavailable': return 'bg-red-100 text-red-700';
      case 'purchase': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const eventTypes = [
    { type: '', label: 'All' },
    { type: 'scan', label: 'Scans' },
    { type: 'confirmed_available', label: 'Available' },
    { type: 'confirmed_unavailable', label: 'Unavailable' },
    { type: 'purchase', label: 'Purchases' },
  ];

  return (
    <div>
      {/* Page Header */}
      <div className="flex justify-between items-start mb-8 pb-5 border-b-2 border-gray-200">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-1">Pin Availability</h2>
          <div className="text-gray-500">Real-time pin availability tracking</div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {loading && events.length === 0 ? (
        <div className="flex items-center justify-center p-12 text-gray-500">
          Loading availability data...
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-5 mb-8">
            <div className="bg-white p-5 rounded-lg shadow">
              <div className="flex justify-between items-start mb-2">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center text-xl">
                  &#x1F4C8;
                </div>
                <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-800">Today</span>
              </div>
              <div className="text-3xl font-bold text-slate-800 mb-0.5">
                {stats.eventsToday.toLocaleString()}
              </div>
              <div className="text-gray-500 text-sm">Events Today</div>
            </div>

            <div className="bg-white p-5 rounded-lg shadow">
              <div className="flex justify-between items-start mb-2">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-xl">
                  &#x1F4C5;
                </div>
                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">7 days</span>
              </div>
              <div className="text-3xl font-bold text-slate-800 mb-0.5">
                {stats.eventsThisWeek.toLocaleString()}
              </div>
              <div className="text-gray-500 text-sm">Events This Week</div>
            </div>

            <div className="bg-white p-5 rounded-lg shadow">
              <div className="flex justify-between items-start mb-2">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center text-xl">
                  &#x1F4CA;
                </div>
              </div>
              <div className="text-3xl font-bold text-slate-800 mb-0.5">
                {stats.totalEvents.toLocaleString()}
              </div>
              <div className="text-gray-500 text-sm">Total Events</div>
            </div>
          </div>

          {/* Event Types Breakdown */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[
              { type: 'scan', label: 'Scans', color: 'orange' },
              { type: 'confirmed_available', label: 'Confirmed Available', color: 'green' },
              { type: 'confirmed_unavailable', label: 'Confirmed Unavailable', color: 'red' },
              { type: 'purchase', label: 'Purchases', color: 'blue' },
            ].map((item) => (
              <div key={item.type} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span dangerouslySetInnerHTML={{ __html: getEventTypeIcon(item.type) }} />
                  <span className="text-sm font-medium text-slate-800">{item.label}</span>
                </div>
                <div className="text-2xl font-bold text-slate-800">
                  {(stats.eventsByType[item.type] || 0).toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex gap-2">
              {eventTypes.map((et) => (
                <button
                  key={et.type}
                  onClick={() => { setEventTypeFilter(et.type); setPage(1); }}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    eventTypeFilter === et.type
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {et.label}
                </button>
              ))}
            </div>
          </div>

          {/* Recent Events */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-slate-700 text-white px-5 py-4 flex justify-between items-center">
              <span className="font-bold">&#x1F4CB; Recent Events</span>
              <span className="text-sm text-gray-300">{events.length} events</span>
            </div>
            <div>
              {events.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-4xl mb-4">&#x1F4CB;</div>
                  <div className="text-xl font-medium text-slate-800 mb-2">No Events</div>
                  <div className="text-gray-500">No availability events recorded yet</div>
                </div>
              ) : (
                events.map((event, index) => (
                  <div
                    key={event.id}
                    className={`flex items-center p-4 ${index !== events.length - 1 ? 'border-b border-gray-200' : ''}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-xl mr-3" dangerouslySetInnerHTML={{ __html: getEventTypeIcon(event.eventType) }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800">{event.pinName || event.pinSku}</div>
                      <div className="text-sm text-gray-500">
                        <span className="mr-2">SKU: {event.pinSku}</span>
                        {event.storeName && (
                          <span>&#x1F4CD; {event.storeName}{event.siteName ? `, ${event.siteName}` : ''}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-bold rounded-full ${getEventTypeBadgeClasses(event.eventType)}`}>
                        {event.eventType.replace(/_/g, ' ')}
                      </span>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">{formatTime(event.createdAt)}</div>
                        {event.scanMethod && (
                          <div className="text-xs text-gray-400">via {event.scanMethod}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <span className="text-gray-600">
                Page {page} of {totalPages}
              </span>
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
